import { useEffect, useState, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CallButton from "components/CallButton";
import { adminApi } from "services/api";
import { useSocket } from "context/SocketContext";
import { lightDark, DARK } from "utils/lightDark";

const WHATSAPP_WINDOW_MS = 24 * 60 * 60 * 1000;

function MediaAttachment({ messageId, index, contentType }) {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    let objectUrl;
    let cancelled = false;
    adminApi
      .getChatMedia(messageId, index)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [messageId, index]);

  if (!src) return <CircularProgress size={16} />;
  if ((contentType || "").startsWith("image/")) {
    return (
      <img
        src={src}
        alt="attachment"
        style={{ maxWidth: 220, borderRadius: 8, display: "block", marginTop: 4 }}
      />
    );
  }
  // maxWidth lets the native 300px player shrink to fit a phone-width bubble.
  return <audio controls src={src} style={{ marginTop: 4, maxWidth: "100%" }} />;
}

MediaAttachment.propTypes = {
  messageId: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  contentType: PropTypes.string,
};
MediaAttachment.defaultProps = { contentType: "" };

function ConversationThread({ conversationId, onConversationUpdate, onBack }) {
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const socket = useSocket();
  const bottomRef = useRef(null);
  const scrollRef = useRef(null);
  const contentRef = useRef(null);
  // True while the reader is at (or near) the newest message; scrolling up to read history
  // turns it off so late-loading media or new messages don't yank them back down.
  const pinnedRef = useRef(true);

  const load = useCallback(async () => {
    pinnedRef.current = true;
    setLoading(true);
    setSendError(null);
    try {
      const res = await adminApi.getChatMessages(conversationId);
      setConversation(res.conversation);
      setMessages(res.messages || []);
      await adminApi.markConversationRead(conversationId);
      if (onConversationUpdate) onConversationUpdate(conversationId);
    } finally {
      setLoading(false);
    }
  }, [conversationId, onConversationUpdate]);

  useEffect(() => {
    if (conversationId) load();
  }, [conversationId, load]);

  useEffect(() => {
    if (!socket) return undefined;
    const handleMessage = ({ conversation: updated, message }) => {
      if (updated._id !== conversationId) return;
      setMessages((prev) => (prev.some((m) => m._id === message._id) ? prev : [...prev, message]));
      setConversation(updated);
    };
    socket.on("chat:message", handleMessage);
    return () => socket.off("chat:message", handleMessage);
  }, [socket, conversationId]);

  // Runs again once loading finishes: while the spinner shows, the message list (and
  // bottomRef) isn't rendered, so a thread used to open at its oldest message.
  useEffect(() => {
    if (loading || !pinnedRef.current) return;
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [messages, loading]);

  // Images and voice notes finish loading after that scroll and push the newest message back
  // out of view — keep the list pinned to the bottom as its content grows.
  useEffect(() => {
    if (loading || typeof ResizeObserver === "undefined") return undefined;
    const scroller = scrollRef.current;
    const content = contentRef.current;
    if (!scroller || !content) return undefined;
    const observer = new ResizeObserver(() => {
      if (pinnedRef.current) scroller.scrollTop = scroller.scrollHeight;
    });
    observer.observe(content);
    return () => observer.disconnect();
  }, [loading]);

  const handleMessagesScroll = () => {
    const el = scrollRef.current;
    if (el) pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const handleSend = async () => {
    if (!draft.trim() || sending) return;
    pinnedRef.current = true; // your own reply always scrolls into view
    setSending(true);
    setSendError(null);
    try {
      await adminApi.sendChatMessage(conversationId, draft.trim());
      setDraft("");
    } catch (err) {
      setSendError(err?.response?.data?.error || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  // Below md the thread replaces the list (see index.js), so it carries its own way back.
  const backButton = onBack ? (
    <IconButton
      onClick={onBack}
      aria-label="Back to conversations"
      sx={{ display: { xs: "inline-flex", md: "none" }, ml: -1, flexShrink: 0 }}
    >
      <Icon>arrow_back</Icon>
    </IconButton>
  ) : null;

  if (!conversationId) {
    return (
      <Card
        sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <MDTypography color="text.secondary">Select a conversation to view the thread</MDTypography>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card
        sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        {backButton && (
          <MDBox position="absolute" top={0} left={0} p={1.5} display={{ xs: "block", md: "none" }}>
            {backButton}
          </MDBox>
        )}
        <CircularProgress />
      </Card>
    );
  }

  const lastInbound = [...messages].reverse().find((m) => m.direction === "inbound");
  // WhatsApp only allows free-form replies within 24h of the customer's last message, so a
  // thread the customer never wrote in (started by staff) is closed from the start.
  const neverWroteIn = conversation?.channel === "whatsapp" && !lastInbound;
  const windowExpired =
    conversation?.channel === "whatsapp" &&
    (neverWroteIn || Date.now() - new Date(lastInbound.createdAt).getTime() > WHATSAPP_WINDOW_MS);

  return (
    <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <MDBox
        p={{ xs: 1.5, md: 2 }}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
      >
        {backButton}
        <MDBox flex={1} minWidth={0}>
          <MDTypography
            variant="h6"
            sx={{
              whiteSpace: { xs: "nowrap", md: "normal" },
              overflow: { xs: "hidden", md: "visible" },
              textOverflow: "ellipsis",
            }}
          >
            {conversation?.displayName || conversation?.customerPhone}
          </MDTypography>
          <MDTypography variant="caption" color="text.secondary">
            {conversation?.customerPhone} ·{" "}
            {conversation?.channel === "whatsapp" ? "WhatsApp" : "SMS"}
          </MDTypography>
        </MDBox>
        <CallButton phoneNumber={conversation?.customerPhone} />
      </MDBox>
      <Divider />

      <MDBox
        ref={scrollRef}
        onScroll={handleMessagesScroll}
        sx={{ flex: 1, overflowY: "auto", p: { xs: 1.5, md: 2 } }}
      >
        <MDBox ref={contentRef}>
          {messages.map((m) => (
            <MDBox
              key={m._id}
              display="flex"
              flexDirection="column"
              alignItems={m.direction === "outbound" ? "flex-end" : "flex-start"}
              mb={1.5}
            >
              <MDBox
                sx={{
                  maxWidth: { xs: "85%", md: "70%" },
                  // long unbroken strings (links, IDs) wrap instead of pushing the thread sideways
                  overflowWrap: "anywhere",
                  borderRadius: 2,
                  px: 1.5,
                  py: 1,
                  backgroundColor:
                    m.direction === "outbound"
                      ? "#1976d2"
                      : lightDark("#f0f0f0", DARK.surfaceHover),
                  color: m.direction === "outbound" ? "#fff" : "inherit",
                }}
              >
                {m.body && (
                  <MDTypography variant="body2" sx={{ color: "inherit" }}>
                    {m.body}
                  </MDTypography>
                )}
                {(m.mediaUrls || []).map((media, idx) => (
                  <MediaAttachment
                    key={`${m._id}-${idx}`}
                    messageId={m._id}
                    index={idx}
                    contentType={media.contentType}
                  />
                ))}
                {m.status === "failed" && (
                  <MDTypography
                    variant="caption"
                    display="block"
                    mt={0.5}
                    sx={{
                      color: m.direction === "outbound" ? "#ffd6d6" : "error.main",
                      fontWeight: 600,
                    }}
                  >
                    Failed: {m.errorMessage}
                  </MDTypography>
                )}
              </MDBox>
              <MDTypography variant="caption" color="text.secondary" mt={0.25}>
                {new Date(m.createdAt).toLocaleString()}
              </MDTypography>
            </MDBox>
          ))}
          <div ref={bottomRef} />
        </MDBox>
      </MDBox>

      {windowExpired && (
        <MDBox px={{ xs: 1.5, md: 2 }}>
          <Alert severity="warning">
            {neverWroteIn
              ? "The customer hasn't messaged this WhatsApp conversation yet — free-form replies will be rejected until they write in (within 24 hours)."
              : "This WhatsApp conversation's 24-hour window has closed — free-form replies will be rejected until the customer messages again."}
          </Alert>
        </MDBox>
      )}
      {sendError && (
        <MDBox px={{ xs: 1.5, md: 2 }} pt={1}>
          <Alert severity="error">{sendError}</Alert>
        </MDBox>
      )}

      <Divider />
      <MDBox
        p={{ xs: 1.5, md: 2 }}
        display="flex"
        gap={1}
        alignItems={{ xs: "flex-end", md: "stretch" }}
      >
        <TextField
          fullWidth
          size="small"
          // 16px text on phones stops iOS Safari zooming the page in when the field is focused
          sx={({ breakpoints }) => ({
            [breakpoints.down("md")]: { "& .MuiInputBase-input": { fontSize: "1rem" } },
          })}
          placeholder="Type a reply…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          multiline
          maxRows={4}
        />
        <MDButton
          color="info"
          variant="gradient"
          onClick={handleSend}
          disabled={sending || !draft.trim()}
        >
          {sending ? <CircularProgress size={18} color="inherit" /> : "Send"}
        </MDButton>
      </MDBox>
    </Card>
  );
}

ConversationThread.propTypes = {
  conversationId: PropTypes.string,
  onConversationUpdate: PropTypes.func,
  onBack: PropTypes.func,
};
ConversationThread.defaultProps = {
  conversationId: null,
  onConversationUpdate: null,
  onBack: null,
};

export default ConversationThread;
